
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Bed,
  Bath,
  Home,
  MapPin,
  Calendar,
  Share2,
  Heart,
  Download,
  Phone,
  Mail,
  Check,
  ArrowRight,
  FileText,
  QrCode,
  FileDown,
  Maximize,
  Armchair,
  Car,
  Building,
  Tag,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';

import ImageGallery from '@/components/ui/ImageGallery';
import FeatureTag from '@/components/ui/FeatureTag';
import PropertyCard from '@/components/ui/PropertyCard';
import { getPropertyBySlug, getDeveloperProperties } from '@/services/property';
import { PropertyType } from '@/types/property';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { createInquiry } from '@/services/inquiry';
import { normalizePhoneNumber, isValidPhoneNumber } from '@/utils/phone';
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords, toSentenceCase } from '@/helper';
import DynamicIcon from '@/components/ui/DynamicIcon';
import { pagination, countryCodes } from '@/constants';
import { DownloadBrochureDialog } from '@/components/shared/DownloadBrochureDialog';
import SEO from '@/components/seo/seo';



const PropertyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<any>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [countryCode, setCountryCode] = useState<string>(countryCodes[0].value);
  const [developerProperties, setDeveloperProperties] = useState<any[]>([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadTab, setDownloadTab] = useState<"brochure" | "floorPlan">("brochure");

  console.log("developerProperties", developerProperties);

  // Load Google Maps API
  const googleApiKey = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey,
    libraries: ['places'],
  });

  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const data = await getPropertyBySlug(id);
        if (data && data.property) {
          const p = data.property;

          // Convert amenities map to array
          const amenitiesList = p.amenities ? Object.keys(p.amenities).filter(k => p.amenities[k]) : [];

          // Map API data to UI structure
          const mappedProperty = {
            id: p._id,
            title: capitalizeWords(p.specifications?.titleDeed || 'Untitled Property'),
            description: p.overview || 'No description available.',
            price: p.pricing?.amount || 0,
            location: capitalizeWords(p.location?.locality || 'Dubai'),
            address: p.location?.address || '',
            unitNo: p.location?.unitNo || '',
            coordinates: {
              latitude: p.location?.coordinates?.latitude || 25.2048, // Default to Dubai
              longitude: p.location?.coordinates?.longitude || 55.2708
            },
            propertyType: capitalizeWords(p.specifications?.propertyType),
            bedrooms: p.specifications?.beds || 0,
            bathrooms: p.specifications?.bathrooms || 0,
            area: p.specifications?.size || 0,
            images: p.media?.images?.length ? p.media.images : ['/placeholder.svg'],
            status: capitalizeWords(p.management?.availability || 'Available'),
            availability: p.management?.availability,
            featured: p.Featured || false,
            trending: p.Trending || false,
            reference: p.management?.reference || p._id.substring(0, 8).toUpperCase(),
            amenities: amenitiesList.length ? amenitiesList : [],
            nearbyAttractions: p.nearbyLocations || [],
            developer: capitalizeWords(p.management?.developer || 'Unknown Developer'),
            furnished: capitalizeWords(p.specifications?.furnishingType || 'Unfurnished'),
            parking: p.specifications?.parkingSpaces || 0,
            floorPlan: encodeS3Url(p.supportFile?.floorPlan || '/placeholder.svg'),
            brochure: p.supportFile?.brochure,
            qrCode: encodeS3Url(p.media?.qrCode || '/placeholder.svg'),
            brokerName: capitalizeWords(p.createdBy?.name || 'Propverse Broker'),
            brokerImage: '/placeholder.svg', // Placeholder if user image missing
            brokerPhone: p.createdBy?.phone || '+971 58 899 5586',
            brokerEmail: p.createdBy?.email || 'leads@urbanoasis.ae',
            slug: p.slug,
            floorPlanList: p.floorplan?.map((fp: any) => ({
              beds: fp.beds,
              size: fp.size,
              price: fp.pricing?.amount,
              currency: fp.pricing?.currency
            })) || [],
            paymentPlan: p.paymentPlan ? {
              planType: p.paymentPlan.planType,
              onBooking: p.paymentPlan.onBooking,
              underConstruction: p.paymentPlan.underConstruction,
              onHandover: p.paymentPlan.onHandover
            } : null
          };

          setProperty(mappedProperty);
          setFormData(prev => ({
            ...prev,
            message: `I'm interested in ${mappedProperty.title} (Ref: ${mappedProperty.reference}). Please provide more information.`
          }));
        } else {
          setError("Property not found");
        }
      } catch (err) {
        console.error("Error fetching property:", err);
        setError("Failed to load property details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  // Fetch developer properties
  useEffect(() => {
    const fetchDevProperties = async () => {
      const devName = property?.developer;

      if (devName && devName !== 'Unknown Developer') {
        try {

          const { data } = await getDeveloperProperties(devName, {
            page: 1,
            limit: pagination.cardLimit
          });

          if (Array.isArray(data)) {
            setDeveloperProperties(data);
          }
        } catch (error) {
          console.error("Error fetching developer properties:", error);
        }
      }
    };

    if (property) {
      fetchDevProperties();
    }
  }, [property]);

  // Format price
  const formatPrice = (price: number) => {
    if (!price || price === 0) return '-';
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Phone validation & normalization
    const fullPhone = countryCode + formData.phone;
    const normalizedPhone = normalizePhoneNumber(fullPhone);
    if (!isValidPhoneNumber(normalizedPhone)) {
      toast({
        variant: "destructive",
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number with country code (e.g., +971501234567)",
      });
      return;
    }


    setIsSubmitting(true);

    try {
      await createInquiry({
        propertyId: property.id,
        name: formData.name,
        email: formData.email,
        phone: normalizedPhone,
        message: formData.message,
        inquiryType: 'property'
      });

      toast({
        title: "Inquiry Sent Successfully",
        description: "Thank you for your interest. A broker will contact you shortly.",
      });

      // Reset form (except message which contains property info)
      setFormData(prev => ({
        ...prev,
        name: '',
        email: '',
        phone: '',
      }));
    } catch (error: any) {
      console.error('Error submitting inquiry:', error);
      toast({
        title: "Failed to Send Inquiry",
        description: error.message || "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle property like
  const toggleLike = () => {
    setIsLiked(!isLiked);
    toast({
      title: isLiked ? "Removed from favorites" : "Added to favorites",
      description: isLiked ? "Property removed from your favorites list." : "Property added to your favorites list.",
    });
  };

  // Share property
  const shareProperty = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: "Link Copied",
      description: "Property link copied to clipboard.",
    });
  };



  // Download floor plan
  const downloadFloorPlan = () => {
    if (!property?.floorPlan || property.floorPlan === '/placeholder.svg') {
      toast({
        title: "Floor Plan Not Available",
        description: "No floor plan is available for this property.",
        variant: "destructive"
      });
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = property.floorPlan;
      link.download = `${property.title}-FloorPlan`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Download Started",
        description: "Floor plan is being downloaded.",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download floor plan. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Download QR code
  const downloadQRCode = () => {
    if (!property?.qrCode || property.qrCode === '/placeholder.svg') {
      toast({
        title: "QR Code Not Available",
        description: "No QR code is available for this property.",
        variant: "destructive"
      });
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = property.qrCode;
      link.download = `${property.title}-QRCode`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Download Started",
        description: "QR code is being downloaded.",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download QR code. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="pt-32 pb-20 flex-1 flex flex-col">
        <div className="container mx-auto px-4 md:px-6">
          {/* Breadcrumb Skeleton */}
          <Skeleton className="h-4 w-64 mb-4" />

          {/* Header Skeleton */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="space-y-2 w-full max-w-lg">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-10 w-3/4" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-10 w-10" />
              <Skeleton className="h-10 w-40 hidden md:block" />
              <Skeleton className="h-10 w-32 hidden md:block" />
            </div>
          </div>

          {/* Gallery Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 h-[85vh] mb-10 overflow-hidden rounded-xl">
            <Skeleton className="md:col-span-2 h-full w-full" />
            <div className="flex flex-col gap-4 h-full">
              <Skeleton className="h-full w-full" />
              <Skeleton className="h-full w-full" />
            </div>
            <div className="hidden md:flex flex-col gap-4 h-full">
              <Skeleton className="h-full w-full" />
              <Skeleton className="h-full w-full" />
            </div>
          </div>

          {/* Content Grid Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {/* Stats Skeleton */}
              <div className="bg-card border rounded-lg p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                  <div className="flex flex-col items-center gap-2"><Skeleton className="h-4 w-20" /><Skeleton className="h-6 w-16" /></div>
                </div>
              </div>

              {/* Description Skeleton */}
              <div className="space-y-4">
                <Skeleton className="h-8 w-48 mb-4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>

            {/* Sidebar Skeleton */}
            <div className="space-y-6">
              <div className="bg-card border rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-4 mb-4">
                  <Skeleton className="h-16 w-16 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                </div>
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          </div>
        </div>

      </div >
    );
  }

  if (error || !property) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        <h2 className="text-2xl font-bold mb-4">Property Not Found</h2>
        <p className="text-muted-foreground mb-6">{error || "The requested property could not be found."}</p>
        <Button asChild>
          <Link to="/properties">Browse Properties</Link>
        </Button>
      </div>

    );
  }

  return (
    <>
      <SEO
        title={toSentenceCase(property.title)}
        description={property.description}
        url={`/properties/${property.slug}`}
        image={property.images[0]}
        keywords={`${property.title}, ${property.location}, ${property.developer}, ${property.type}, ${property.furnished}, ${property.property_type}, dubai, real estate, properties, apartments, houses, villas, condos, dubai real estate, dubai apartments, dubai houses, dubai villas, dubai condos`}
      />
      <div className="flex flex-col flex-1">

        {/* Property Details */}
        <div className="pt-32 pb-20">
          <div className="container mx-auto px-4 md:px-6">
            {/* Breadcrumbs */}
            <div className="flex text-sm text-muted-foreground mb-4">
              <Link to="/" className="hover:text-foreground">Home</Link>
              <span className="mx-2">/</span>
              <Link to="/properties" className="hover:text-foreground">Properties</Link>
              <span className="mx-2">/</span>
              <span className="font-medium text-foreground">{property.title}</span>
            </div>

            {/* Property Title & Actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{property.title}</h1>
                <div className="flex items-center text-sm text-muted-foreground">
                  <span>By {property.developer}</span>
                  <span className="mx-2">|</span>
                  <MapPin className="h-4 w-4 text-gold mr-1" />
                  <span>{property.location}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={shareProperty}
                >
                  <Share2 className="h-5 w-5" />
                </Button>
                {property.brochure && (
                  <Button
                    variant="outline"
                    className="hidden md:flex items-center gap-2"
                    onClick={() => {
                      setDownloadTab("brochure");
                      setIsDownloadModalOpen(true);
                    }}
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    <span>Download Brochure</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={downloadQRCode}
                  className="hidden md:flex items-center gap-2"
                  disabled={!property.qrCode || property.qrCode === '/placeholder.svg'}
                >
                  <QrCode className="h-4 w-4" />
                  <span>Download QR</span>
                </Button>
              </div>
            </div>

            <div className="mb-8">
              <div className="border rounded-lg p-4 relative overflow-hidden inline-block shadow-sm pr-10">
                <div className="flex flex-col">
                  <span className="text-sm text-muted-foreground mb-1">Starting from</span>
                  <div className="text-4xl md:text-4xl font-extrabold text-gold tracking-tight">
                    {formatPrice(property.price)}
                  </div>
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <div className="mb-10">
              <ImageGallery
                images={property.images}
                title={property.title}
                qrCode={property.qrCode}
              />
            </div>

            {/* Property Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
              <div className="lg:col-span-2">
                {/* Property Features */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-b border-border pb-6 mb-6">
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Home className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Property Type</div>
                      <div className="font-semibold">{property.propertyType}</div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Bed className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Bedrooms</div>
                      <div className="font-semibold">
                        {property.bedrooms || "-"}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Bath className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Bathrooms</div>
                      <div className="font-semibold">
                        {property.bathrooms || "-"}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Maximize className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Area</div>
                      <div className="font-semibold">
                        {property.area || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Armchair className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Furnished</div>
                      <div className="font-semibold">{property.furnished}</div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Car className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Parking</div>
                      <div className="font-semibold">
                        {property.parking || "-"}
                      </div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Building className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Developer</div>
                      <div className="font-semibold">{property.developer}</div>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-muted/20 transition-colors">
                      <div className="p-3 rounded-xl bg-secondary/50 mb-3 text-gold">
                        <Tag className="h-6 w-6 text-foreground" />
                      </div>
                      <div className="text-muted-foreground text-sm mb-1">Price</div>
                      <div className="font-semibold">{formatPrice(property.price)}</div>
                    </div>
                  </div>
                </div>

                {/* Tabs: Description, Amenities, etc */}
                <Tabs defaultValue="description" className="mb-8">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="description">Description</TabsTrigger>
                    <TabsTrigger value="amenities">Amenities</TabsTrigger>
                    <TabsTrigger value="location">Location</TabsTrigger>
                  </TabsList>

                  <TabsContent value="description" className="bg-white rounded-lg shadow-md p-6 mt-4">
                    <h3 className="text-xl font-semibold mb-4">Property Description</h3>
                    <div className="text-muted-foreground whitespace-pre-wrap">
                      {property.description}
                    </div>

                    <Separator className="my-6" />

                    <div>
                      <h4 className="text-lg font-semibold mb-4">Property Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Property ID:</span>
                          <span className="font-medium">{property.reference}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Property Type:</span>
                          <span className="font-medium">{property.propertyType}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Price:</span>
                          <span className="font-medium">
                            {formatPrice(property.price)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Area:</span>
                          <span className="font-medium">
                            {property.area ? `${property.area} sqft` : "-"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Bedrooms:</span>
                          <span className="font-medium">{property.bedrooms || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Bathrooms:</span>
                          <span className="font-medium">
                            {property.bathrooms || "-"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Furnished:</span>
                          <span className="font-medium">{property.furnished}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Developer:</span>
                          <span className="font-medium">{property.developer}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="amenities" className="bg-white rounded-lg shadow-md p-6 mt-4">
                    <h3 className="text-xl font-semibold mb-4">Property Amenities</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {property.amenities.map((amenity: string) => (
                        <div key={amenity} className="flex items-center">
                          <DynamicIcon name={amenity} className="h-5 w-5 text-gold mr-2" />
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>

                    <Separator className="my-6" />

                    <h3 className="text-xl font-semibold mb-4">Nearby Attractions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {property.nearbyAttractions.map((attraction) => (
                        <div key={attraction} className="flex items-center">
                          <MapPin className="h-5 w-5 text-gold mr-2" />
                          <span>{attraction}</span>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="location" className="bg-white rounded-lg shadow-md p-6 mt-4">
                    <h3 className="text-xl font-semibold mb-4">Location</h3>
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                      {isMapLoaded && property?.coordinates ? (
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{
                            lat: property.coordinates.latitude,
                            lng: property.coordinates.longitude
                          }}
                          zoom={15}
                          options={{
                            disableDefaultUI: false,
                            zoomControl: true,
                            streetViewControl: true,
                            mapTypeControl: true,
                          }}
                        >
                          <Marker
                            position={{
                              lat: property.coordinates.latitude,
                              lng: property.coordinates.longitude
                            }}
                            title={property.title}
                          />
                        </GoogleMap>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-secondary">
                          <MapPin className="h-8 w-8 text-muted-foreground mr-2" />
                          <span className="text-muted-foreground">
                            {isMapLoaded ? 'Loading map...' : 'Map of ' + property.location}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 space-y-2">
                      <h4 className="text-lg font-semibold mb-2">Address</h4>
                      {property.address && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Address:</span> {property.address}
                        </p>
                      )}
                      {property.unitNo && (
                        <p className="text-muted-foreground">
                          <span className="font-medium">Unit No:</span> {property.unitNo}
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        <span className="font-medium">Locality:</span> {capitalizeWords(property.location)}, Dubai, UAE
                      </p>
                      {property.coordinates && (
                        <p className="text-muted-foreground text-sm">
                          <span className="font-medium">Coordinates:</span> {property.coordinates.latitude.toFixed(6)}, {property.coordinates.longitude.toFixed(6)}
                        </p>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Floor Plan */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4">Floor Plans</h3>

                  {/* Floor Plan List as Boxes/Cards */}
                  {property.floorPlanList && property.floorPlanList.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {property.floorPlanList.map((plan: any, idx: number) => (
                        <div key={idx} className="border rounded-lg p-4 bg-card relative overflow-hidden">
                          {/* Decorative accent */}
                          {/* <div className="absolute top-0 left-0 w-1 h-full bg-primary/20"></div> */}

                          <div className="flex flex-col h-full pl-3">
                            <div className="font-semibold text-lg mb-4 text-foreground">
                              {capitalizeWords(plan.beds)}
                            </div>

                            <div className="flex items-center justify-between mt-auto pt-2 border-t border-dashed">
                              <div className="flex flex-col">
                                <span className="text-sm text-muted-foreground">Size</span>
                                <span className="font-medium text-foreground">{plan.size ? `${plan.size} sqft` : '-'}</span>
                              </div>
                              <div className="h-8 w-px bg-border mx-2"></div>
                              <div className="flex flex-col text-right">
                                <span className="text-sm text-muted-foreground">Price</span>
                                <span className="font-medium text-primary">{plan.price}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Floor Plan Preview & Download */}
                  {/* Floor Plan Preview & Download */}
                  {property.floorPlan && property.floorPlan !== '/placeholder.svg' ? (
                    <div className="mt-4">
                      <Button variant="outline" onClick={() => {
                        setDownloadTab("floorPlan");
                        setIsDownloadModalOpen(true);
                      }} className="w-full sm:w-auto">
                        <Download className="h-4 w-4 mr-2" />
                        Download Floor Plan
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground py-6 bg-muted/20 rounded-lg mt-4">
                      No floor plan file available.
                    </div>
                  )}
                </div>



                {/* Payment Plan */}
                {property.paymentPlan && (
                  <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h3 className="text-xl font-semibold mb-4">Payment Plan</h3>
                    <div className="border rounded-lg p-6 bg-card relative overflow-hidden">
                      {/* <div className="absolute top-0 left-0 w-1 h-full bg-primary/20"></div> */}

                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pl-4">
                        <div className="space-y-1">
                          <div className="text-sm text-muted-foreground">Plan Type</div>
                          <div className="text-2xl font-bold text-foreground">
                            {property.paymentPlan.planType ? (property.paymentPlan.planType) : 'Standard Plan'}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full md:w-auto">
                          <div className="flex flex-col p-3 bg-secondary/10 rounded-md">
                            <span className="text-sm text-muted-foreground mb-1">On Booking</span>
                            <span className="font-bold text-lg text-primary">{property.paymentPlan.onBooking || '-'}</span>
                          </div>
                          <div className="flex flex-col p-3 bg-secondary/10 rounded-md">
                            <span className="text-sm text-muted-foreground mb-1">During Construction</span>
                            <span className="font-bold text-lg text-primary">{property.paymentPlan.underConstruction || '-'}</span>
                          </div>
                          <div className="flex flex-col p-3 bg-secondary/10 rounded-md">
                            <span className="text-sm text-muted-foreground mb-1">On Handover</span>
                            <span className="font-bold text-lg text-primary">{property.paymentPlan.onHandover || '-'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* QR Code */}
                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                  <h3 className="text-xl font-semibold mb-4">Property QR Code</h3>
                  <div className="flex flex-col items-center">
                    <div className="w-48 h-48 bg-white rounded-lg shadow mb-4 overflow-hidden">
                      <img
                        src={property.qrCode}
                        alt="Property QR Code"
                        className="w-full h-full p-3"
                      />
                    </div>
                    <p className="text-muted-foreground text-center mb-4">
                      Scan this QR code with your smartphone to view this property on your mobile device.
                    </p>
                    <Button
                      variant="outline"
                      onClick={downloadQRCode}
                      disabled={!property.qrCode || property.qrCode === '/placeholder.svg'}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download QR Code
                    </Button>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-1 sticky top-32 h-fit">
                {/* Contact Broker Card */}
                <Card className="mb-8">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 mb-6">
                      <img
                        src={property.brokerImage}
                        alt={property.brokerName}
                        className="w-16 h-16 rounded-full object-cover border-2 border-gold"
                      />
                      <div>
                        <h3 className="font-semibold">{property.brokerName}</h3>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <a
                        href={`tel:${property.brokerPhone}`}
                        className="flex items-center text-sm"
                      >
                        <Phone className="h-4 w-4 text-gold mr-2" />
                        {property.brokerPhone}
                      </a>
                      <a
                        href={`mailto:${property.brokerEmail}`}
                        className="flex items-center text-sm"
                      >
                        <Mail className="h-4 w-4 text-gold mr-2" />
                        {property.brokerEmail}
                      </a>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <Input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                      <Input
                        type="email"
                        name="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                      <div className="flex gap-2">
                        <Select value={countryCode} onValueChange={setCountryCode}>
                          <SelectTrigger className="w-[110px]">
                            <SelectValue placeholder="Code" />
                          </SelectTrigger>
                          <SelectContent>
                            {countryCodes.map((code) => (
                              <SelectItem key={code.label} value={code.value}>
                                {code.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="tel"
                          name="phone"
                          placeholder="Your Phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className="flex-1"
                        />
                      </div>
                      <Textarea
                        name="message"
                        placeholder="Your Message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        required
                      />
                      <Button type="submit" className="w-full" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Mortgage Calculator Widget */}
                {property.availability !== 'OFF-PLAN' && (
                  <Card className="mb-8">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-4">Mortgage Calculator</h3>

                      <div className="space-y-4 mb-6">
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            Property Price
                          </label>
                          <Input
                            type="text"
                            disabled
                            value={formatPrice(property.price)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            Down Payment (20%)
                          </label>
                          <Input
                            type="text"
                            disabled
                            value={formatPrice(property.price * 0.2)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            Loan Amount
                          </label>
                          <Input
                            type="text"
                            disabled
                            value={formatPrice(property.price * 0.8)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground mb-1 block">
                            Estimated Monthly Payment
                          </label>
                          <Input
                            type="text"
                            disabled
                            value={formatPrice((property.price * 0.8) / 240)}
                          />
                          <div className="text-xs text-muted-foreground mt-1">
                            Based on 20 years at 4% interest rate
                          </div>
                        </div>
                      </div>

                      <Link to="/mortgage-calculator" className="flex items-center text-sm font-medium text-primary hover:text-gold">
                        Advanced Calculator <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </CardContent>
                  </Card>
                )}

                {/* Property Features Tags */}
                {/* <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Property Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.length > 0 ? (
                      property.amenities.map((amenity: string) => (
                        <FeatureTag key={amenity} label={amenity} />
                      ))
                    ) : (
                      <span className="text-muted-foreground">No specific features listed.</span>
                    )}
                  </div>
                </CardContent>
              </Card> */}
              </div>
            </div>

            {/* More Properties by Developer */}
            {developerProperties.length > 0 && (
              <div className="mb-10 mt-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">
                    More properties by <Link to={`/developers/${encodeURIComponent(property.developer)}`} className="text-primary underline">{property.developer}</Link>
                  </h2>
                  <Button variant="outline" asChild>
                    <Link to={`/developers/${encodeURIComponent(property.developer)}`}>View All</Link>
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {developerProperties.map((prop: any) => (
                    <PropertyCard key={prop._id} property={prop} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DownloadBrochureDialog
          open={isDownloadModalOpen}
          onOpenChange={setIsDownloadModalOpen}
          property={property}
          initialTab={downloadTab}
        />
      </div >
    </>
  );
};

export default PropertyDetail;
